import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Download, Send, DollarSign, CheckCircle, Image as ImageIcon, FileText } from 'lucide-react';
import LeadCaptureModal from '../LeadCaptureModal';
import SendQuoteModal from '../SendQuoteModal';
import PaywallModal from '../PaywallModal';
import SocialShare from '../SocialShare';
import ContractEditor from './ContractEditor';
import { useQuoteActions } from './useQuoteActions';
import { useToast } from '../../hooks/use-toast';

/**
 * Pure presentation component for quote action buttons
 * All logic handled by useQuoteActions hook
 */
const QuoteActionButtons = ({
  quote,
  customerName,
  projectType,
  squareFeet,
  laborRate,
  markup,
  selectedTemplate,
  branding,
  projectPhotos,
  getProjectTypeData,
  quotaInfo,
  checkQuota
}) => {
  const { toast } = useToast();
  const [showContractEditor, setShowContractEditor] = useState(false);
  const actions = useQuoteActions({
    quote,
    customerName,
    projectType,
    squareFeet,
    laborRate,
    markup,
    selectedTemplate,
    branding,
    projectPhotos,
    getProjectTypeData,
    quotaInfo,
    checkQuota
  });

  return (
    <div className="space-y-3">
      {/* Main Actions */}
      <Button 
        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
        onClick={actions.handleDownloadPDF}
      >
        <Download className="w-4 h-4 mr-2" />
        Download PDF Quote
      </Button>
      
      <Button 
        variant="outline" 
        className="w-full bg-slate-800 border-slate-600 hover:bg-slate-700 text-white"
        onClick={actions.handleSendQuote}
      >
        <Send className="w-4 h-4 mr-2" />
        Send to Customer
      </Button>
      
      {/* Convert to Invoice */}
      {actions.quoteId && (
        <div className="pt-4 border-t border-slate-700">
          <div className="bg-green-900/30 border-2 border-green-500 rounded-lg p-4 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-sm font-semibold text-green-300">Quote Saved!</span>
            </div>
            <p className="text-xs text-green-200">Convert to invoice and get paid faster</p>
          </div>
          
          <Button 
            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold shadow-lg"
            onClick={actions.handleConvertToInvoice}
            disabled={actions.convertingToInvoice}
          >
            {actions.convertingToInvoice ? (
              <>Processing...</>
            ) : (
              <>
                <DollarSign className="w-4 h-4 mr-2" />
                Convert to Invoice → Get Paid
              </>
            )}
          </Button>
          <p className="text-xs text-slate-400 text-center mt-2">
            One click. Same data. Professional invoice.
          </p>

          {/* Social Sharing */}
          <div className="mt-6 pt-4 border-t border-slate-700">
            <SocialShare 
              quote={quote}
              customerName={customerName || actions.leadData?.name}
            />
          </div>

          {/* Contract Editor */}
          <div className="mt-4">
            <Button
              onClick={() => setShowContractEditor(true)}
              variant="outline"
              className="w-full border-amber-500 text-amber-300 hover:bg-amber-950"
              size="sm"
              data-testid="generate-contract-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Generate & Edit Contract
            </Button>
            <p className="text-xs text-slate-400 text-center mt-2">
              8-section legally-binding agreement — signed via client portal
            </p>
          </div>

          {/* Quote Image Download */}
          <div className="mt-4">
            <Button
              onClick={actions.handleDownloadQuoteImage}
              variant="outline"
              className="w-full border-blue-500 text-blue-400 hover:bg-blue-950"
              size="sm"
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              Download Quote Image
            </Button>
            <p className="text-xs text-slate-400 text-center mt-2">
              Perfect for Instagram, Facebook & LinkedIn
            </p>
          </div>
        </div>
      )}

      {/* Modals */}
      <LeadCaptureModal
        isOpen={actions.showLeadModal}
        onClose={() => actions.setShowLeadModal(false)}
        onSubmit={actions.handleLeadSubmit}
        quoteData={{
          ...quote,
          customerName: customerName
        }}
      />

      {actions.showSendModal && actions.leadData && quote && (
        <SendQuoteModal
          quote={{
            id: actions.quoteId,
            project_name: `${getProjectTypeData().label} - ${customerName || actions.leadData.name}`,
            project_type: projectType,
            square_feet: squareFeet,
            total: quote.total
          }}
          lead={{
            id: actions.leadData.id || actions.quoteId,
            name: actions.leadData.name,
            email: actions.leadData.email,
            phone: actions.leadData.phone
          }}
          onClose={() => actions.setShowSendModal(false)}
          onSuccess={() => {
            toast({
              title: "Quote Sent Successfully!",
              description: "Client will receive it via SMS + Email",
              duration: 5000,
            });
          }}
        />
      )}

      <PaywallModal
        isOpen={actions.showPaywallModal}
        onClose={() => actions.setShowPaywallModal(false)}
        quoteData={quote}
      />

      <ContractEditor
        quote={{
          id: actions.quoteId,
          deposit_percent: 50,
        }}
        open={showContractEditor && !!actions.quoteId}
        onClose={() => setShowContractEditor(false)}
      />
    </div>
  );
};

export default QuoteActionButtons;
